/**
 * Product types
 */

import { TechinicalOutputDTO } from "../techinicaldocument/types";
import { TemplateOutputDTO } from "../template/types";
import { PhaseFormFieldData } from "../phaseForm/types";
import type { MethodPaymentEnum } from "@/schemas/methodpayment";

export enum ActivityType {
  FORM = "form",
  EXTERNAL = "external",
  DOCUMENT_GENERATION = "document_generation",
}

export type IFinancialConfig = {
  cashPrice?: number;
  installmentPrice?: number;
  paymentMethods?: MethodPaymentEnum[]; // "PIX", "CREDIT_CARD", "BOLETO", "TED", "DOC", "RECURRING_CREDIT_CARD"
  maxInstallments?: number;
  interestFreeInstallments?: number;
  interestRate?: number;
  standardCashDiscount?: number;
  standardInstallmentDiscount?: number;
  managerOverrideDiscount?: number;
};

export type ISubprocessGenerator = {
  _id?: string;
  fieldId: string;
  maxSubprocesses?: number;
  minValues?: number;
  maxValues?: number;
  autoGenerate: boolean;
  requireApproval: boolean;
  createdAt?: string;
  updatedAt?: string;
  financialConfig?: IFinancialConfig;
};

export type IFormField = PhaseFormFieldData & {
  fieldId: string; // Unique identifier in the product context
};

export type IForm = {
  _id?: string;
  fields: IFormField[];
  createdAt?: string;
  updatedAt?: string;
  financialConfig?: IFinancialConfig;
};

export type IActivity = {
  _id?: string;
  name: string;
  description: string;
  image?: string;
  hours?: number;
  days?: number;
  order: number;
  type: ActivityType;
  form?: IForm; // Required when type is 'form'
  prerequisites?: string[]; // IDs of prerequisite activities
  duration: number; // Duration in minutes
  typeRefId?: string; // Reference ID for external types (forms, etc)
  createdAt?: string;
  updatedAt?: string;
  tutorial?: {
    text?: { link?: string; name?: string };
    video?: { link?: string; name?: string };
    audio?: { link?: string; name?: string };
    pdf?: { link?: string; name?: string };
  };
  documents?: string[];
  templates?: (string | TemplateOutputDTO)[];
  techinicals?: (string | TechinicalOutputDTO)[];
  financialConfig?: IFinancialConfig;
};

export type IProductPhase = {
  _id?: string;
  name: string;
  type: string;

  image?: string;
  description: string;
  order: number;
  activities: IActivity[];
  subprocessGenerators: ISubprocessGenerator[];
  tutorial?: {
    text?: { link?: string; name?: string };
    video?: { link?: string; name?: string };
    audio?: { link?: string; name?: string };
    pdf?: { link?: string; name?: string };
  };
  createdAt?: string;
  updatedAt?: string;
  financialConfig?: IFinancialConfig;
};

export type IProduct = {
  _id?: string;
  name: string;
  number_product: string;
  image?: string;
  key: string;
  description?: string;
  phases: IProductPhase[];
  matters?: string[];
  tutorial?: {
    text?: { link?: string; name?: string };
    video?: { link?: string; name?: string };
    audio?: { link?: string; name?: string };
    pdf?: { link?: string; name?: string };
  };
  createdAt?: string;
  updatedAt?: string;
  financialConfig?: IFinancialConfig;
};

export type IValidationError = {
  code: string;
  message: string;
  field?: string;
  details?: Record<string, unknown>;
};

export type ISubprocessFieldValidation = {
  fieldId: string;
  isValid: boolean;
  errors?: IValidationError[];
};

export type IPrerequisiteValidation = {
  activityId: string;
  isValid: boolean;
  errors?: IValidationError[];
};

export enum ErrorCode {
  INVALID_PRODUCT_NAME = "INVALID_PRODUCT_NAME",
  DUPLICATE_KEY = "DUPLICATE_KEY",
  INVALID_KEY_FORMAT = "INVALID_KEY_FORMAT",
  NO_PHASES = "NO_PHASES",
  INVALID_MATTER_ID = "INVALID_MATTER_ID",
  MISSING_PHASE_NAME = "MISSING_PHASE_NAME",
  MISSING_PHASE_DESCRIPTION = "MISSING_PHASE_DESCRIPTION",
  DUPLICATE_PHASE_ORDER = "DUPLICATE_PHASE_ORDER",
  MISSING_ACTIVITY_NAME = "MISSING_ACTIVITY_NAME",
  MISSING_ACTIVITY_DESCRIPTION = "MISSING_ACTIVITY_DESCRIPTION",
  DUPLICATE_ACTIVITY_ORDER = "DUPLICATE_ACTIVITY_ORDER",
  INVALID_ACTIVITY_TYPE = "INVALID_ACTIVITY_TYPE",
  MISSING_FORM = "MISSING_FORM",
  NO_FORM_FIELDS = "NO_FORM_FIELDS",
  INVALID_PREREQUISITE = "INVALID_PREREQUISITE",
  CYCLIC_PREREQUISITES = "CYCLIC_PREREQUISITES",
  DUPLICATE_FIELD_ID = "DUPLICATE_FIELD_ID",
  INVALID_SUBPROCESS_CONFIG = "INVALID_SUBPROCESS_CONFIG",
  INVALID_FIELD_REFERENCE = "INVALID_FIELD_REFERENCE",
}
